<template>
  <teleport to="body">
    <div
      class="lb"
      @click.self="$emit('close')"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <!-- Закрыть -->
      <button class="lb__close" @click="$emit('close')" aria-label="Закрыть">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>

      <!-- Счётчик -->
      <div v-if="items.length > 1" class="lb__counter">
        {{ current + 1 }}&thinsp;/&thinsp;{{ items.length }}
      </div>

      <!-- Стрелка назад -->
      <button
        v-if="items.length > 1"
        class="lb__arrow lb__arrow--prev"
        @click="prev"
        aria-label="Предыдущее"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      <!-- Медиа -->
      <div class="lb__stage" @click.self="$emit('close')">
        <template v-if="currentItem">
          <!-- Изображение -->
          <img
            v-if="isImage(currentItem)"
            :src="currentItem.src"
            :alt="currentItem.alt || ''"
            :key="current"
            class="lb__img"
            @click.stop
          />
          <!-- Видео (mp4) -->
          <video
            v-else-if="currentItem.type === 'video'"
            :src="currentItem.src"
            :key="`v${current}`"
            class="lb__video"
            controls
            autoplay
            @click.stop
          />
          <!-- YouTube / Vimeo (iframe) -->
          <iframe
            v-else-if="currentItem.type === 'embed'"
            :src="currentItem.src"
            :key="`e${current}`"
            class="lb__embed"
            allowfullscreen
            frameborder="0"
            @click.stop
          />
        </template>
      </div>

      <!-- Стрелка вперёд -->
      <button
        v-if="items.length > 1"
        class="lb__arrow lb__arrow--next"
        @click="next"
        aria-label="Следующее"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </div>
  </teleport>
</template>

<script setup>
const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  startIndex: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['close'])

const current = ref(props.startIndex)
const currentItem = computed(() => props.items[current.value] ?? null)

function isImage(item) {
  return !item.type || item.type === 'image'
}

function next() {
  current.value = (current.value + 1) % props.items.length
}

function prev() {
  current.value = (current.value - 1 + props.items.length) % props.items.length
}

// Клавиатура
function onKeyDown(e) {
  if (e.key === 'Escape') emit('close')
  if (e.key === 'ArrowRight') next()
  if (e.key === 'ArrowLeft') prev()
}

// Свайп
let touchStartX = 0
function onTouchStart(e) {
  touchStartX = e.touches[0].clientX
}
function onTouchEnd(e) {
  const dx = e.changedTouches[0].clientX - touchStartX
  if (Math.abs(dx) > 48) dx > 0 ? prev() : next()
}

onMounted(() => {
  document.addEventListener('keydown', onKeyDown)
  document.body.style.overflow = 'hidden'
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeyDown)
  document.body.style.overflow = ''
})
</script>

<style scoped lang="scss">
.lb {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;

  &__stage {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 6rem 10rem;
    box-sizing: border-box;

    @media (max-width: 48em) {
      padding: 6rem 1.6rem;
    }
  }

  &__img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 0.4rem;
    display: block;
    user-select: none;
  }

  &__video,
  &__embed {
    max-width: 100%;
    max-height: 100%;
    width: 96rem;
    aspect-ratio: 16 / 9;
    border-radius: 0.4rem;
  }

  &__close {
    position: absolute;
    top: 2rem;
    right: 2rem;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    width: 4.4rem;
    height: 4.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #fff;
    transition: background 0.2s;
    z-index: 2;

    &:hover {
      background: rgba(255, 255, 255, 0.22);
    }
  }

  &__counter {
    position: absolute;
    top: 2.4rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 1.4rem;
    color: rgba(255, 255, 255, 0.55);
    letter-spacing: 0.05em;
    pointer-events: none;
    z-index: 2;
  }

  &__arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    width: 5.2rem;
    height: 5.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #fff;
    transition: background 0.2s;
    z-index: 2;

    &:hover {
      background: rgba(255, 255, 255, 0.22);
    }

    &--prev {
      left: 2rem;
    }

    &--next {
      right: 2rem;
    }

    @media (max-width: 48em) {
      width: 4rem;
      height: 4rem;

      &--prev { left: 0.8rem; }
      &--next { right: 0.8rem; }
    }
  }
}
</style>
